'use client';

import { AnimatePresence, motion } from 'motion/react';
import { type PropsWithChildren, useMemo, useState } from 'react';
import { FocusScope } from 'react-aria';

import CloseX from '~/assets/icons/close-x.svg';
import {
  DialogContext,
  DialogContextValue,
} from '~/lib/hooks/useDialogContext';
import { cn } from '~/lib/utils/classname';

export interface Props extends PropsWithChildren {
  trigger: (openDialog: () => void) => React.ReactNode;
  className?: string;
}

const Dialog: React.FC<Props> = ({ trigger, className, children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const context = useMemo<DialogContextValue>(
    () => ({
      close: () => setIsOpen(false),
    }),
    []
  );

  return (
    <>
      {trigger(() => setIsOpen(true))}

      <div
        className={cn(
          'fixed top-0 left-0 isolate z-40 flex h-full w-full items-center justify-center pt-20 pb-4 lg:pt-0 lg:pb-0',
          {
            'pointer-events-none': !isOpen,
          }
        )}
      >
        <AnimatePresence>
          {isOpen && (
            <FocusScope contain restoreFocus autoFocus>
              <motion.button
                className="bg-brand-tertiary/80 absolute top-0 left-0 h-full w-full backdrop-blur-sm"
                type="button"
                aria-label="Fermer"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                data-prevent="lenis"
                /**
                 * XXX: Exclude from tab index for accessibility, there is already
                 * another close button that is visible on screen, having this
                 * second overlay is more confusing than anything
                 */
                tabIndex={-1}
              />

              <motion.dialog
                key="modal"
                className={cn(
                  'pointer-events-auto relative z-10 mx-2 bg-white max-lg:after:pointer-events-none max-lg:after:absolute max-lg:after:top-0 max-lg:after:left-0 max-lg:after:z-10 max-lg:after:h-full max-lg:after:w-full max-lg:after:bg-[linear-gradient(180deg,white_12px,transparent_32px,transparent_calc(100%-32px),white_calc(100%-12px))] lg:mx-0',
                  className
                )}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                data-prevent="lenis"
                open
              >
                <button
                  type="button"
                  aria-label="Fermer"
                  className="text-brand-primary absolute top-1 right-1 z-20 cursor-pointer [&>svg]:w-7"
                  onClick={() => setIsOpen(false)}
                >
                  <CloseX />
                </button>

                <div className="relative max-h-[calc(100svh-80px)] overflow-y-auto px-4 py-8 lg:px-16 lg:py-10">
                  <DialogContext.Provider value={context}>
                    {children}
                  </DialogContext.Provider>
                </div>
              </motion.dialog>
            </FocusScope>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default Dialog;
